import { format, formatDistanceToNowStrict, parseISO } from 'date-fns'
import { dateLocale, dfLocale, t } from '@/lib/i18n'

/** Parse a UTC ISO 8601 string into a Date in the local timezone. */
export function parseUtc(iso: string | null | undefined): Date | null {
  if (!iso) return null
  try {
    const d = parseISO(iso)
    return Number.isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

/** "HH:MM" in the local timezone. Returns "—" for null/invalid input. */
export function formatLocalTime(iso: string | null | undefined): string {
  const d = parseUtc(iso)
  return d ? format(d, 'HH:mm') : '—'
}

/** "YYYY-MM-DD HH:MM" in the local timezone. */
export function formatLocalDateTime(iso: string | null | undefined): string {
  const d = parseUtc(iso)
  return d ? format(d, 'yyyy-MM-dd HH:mm') : '—'
}

/** "YYYY-MM-DD" in the local timezone. */
export function formatLocalDate(iso: string | null | undefined): string {
  const d = parseUtc(iso)
  return d ? format(d, 'yyyy-MM-dd') : '—'
}

/**
 * Calendar date in a given IANA timezone as `YYYY-MM-DD`. Powers the
 * WEB-021 site dashboard date selector, which needs "today in Athens"
 * to differ from "today in the browser" during the 2-3 hours the two
 * zones disagree about the calendar day.
 *
 * `iso=null`/omitted → uses `new Date()` (now). Bad input falls back to
 * the browser's local calendar date — callers never see a throw.
 */
export function localDateInTz(tz: string | null, iso?: string | null): string {
  const d = iso ? parseUtc(iso) : new Date()
  if (!d || Number.isNaN(d.getTime())) return format(new Date(), 'yyyy-MM-dd')
  if (!tz) return format(d, 'yyyy-MM-dd')
  try {
    // `en-CA` formats as `YYYY-MM-DD` — the only locale that guarantees this.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d)
  } catch {
    return format(d, 'yyyy-MM-dd')
  }
}

/**
 * "Latest observation" label for the Zonoo Site Update card, in the
 * PROJECT's own timezone (never the viewer's browser timezone -- a site
 * in Madrid and a viewer in New York must see the same "Today, 10:12").
 * "Today, HH:MM" when the observation falls on the project's current
 * calendar day; otherwise "D MMM, HH:MM" so an older observation is
 * never mistaken for a fresh one. Returns "—" for null/invalid input.
 */
export function formatObservedLabel(iso: string | null | undefined, tz: string | null): string {
  const d = parseUtc(iso)
  if (!d) return '—'
  const obsDay = localDateInTz(tz, iso)
  const today = localDateInTz(tz)
  try {
    const timePart = tz
      ? new Intl.DateTimeFormat(dateLocale(), { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
      : format(d, 'HH:mm')
    if (obsDay === today) return t('Today, {time}', { time: timePart })
    const datePart = tz
      ? new Intl.DateTimeFormat(dateLocale(), { timeZone: tz, day: 'numeric', month: 'short' }).format(d)
      : format(d, 'd MMM', { locale: dfLocale() })
    return `${datePart}, ${timePart}`
  } catch {
    return obsDay === today ? t('Today, {time}', { time: format(d, 'HH:mm') }) : `${format(d, 'd MMM', { locale: dfLocale() })}, ${format(d, 'HH:mm')}`
  }
}

/** Relative ago label, e.g. "5 minutes ago". */
export function formatRelative(iso: string | null | undefined): string {
  const d = parseUtc(iso)
  return d ? `${formatDistanceToNowStrict(d, { addSuffix: true, locale: dfLocale() })}` : '—'
}

/** Format a USD amount with up to 4 fractional digits. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `$${value.toFixed(value < 1 ? 4 : 2)}`
}

/** Compact integer formatter (1.2k, 3.4M). */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}k`
  return value.toString()
}

const _intFormatter = new Intl.NumberFormat('en-US')

/** Integer with thousand separators, "—" for null. */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return _intFormatter.format(value)
}

/**
 * Group an array of items by the local-hour (HH:00) of a UTC ISO timestamp.
 * Both the hour buckets and the items within each bucket are sorted
 * descending by timestamp (newest first), independent of input order.
 */
export function groupByLocalHour<T>(
  items: T[],
  getTimestamp: (item: T) => string,
): Array<{ hour: string; items: T[] }> {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const d = parseUtc(getTimestamp(item))
    const hour = d ? format(d, 'HH:00') : '?'
    const bucket = groups.get(hour)
    if (bucket) {
      bucket.push(item)
    } else {
      groups.set(hour, [item])
    }
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([hour, bucketItems]) => ({
      hour,
      items: [...bucketItems].sort((a, b) => {
        const ta = getTimestamp(a) ?? ''
        const tb = getTimestamp(b) ?? ''
        return ta < tb ? 1 : ta > tb ? -1 : 0
      }),
    }))
}
